import { useCallback, useEffect, useState } from "react";
import { copyText } from "../../components/player";
import { admin } from "../../api/admin";
import type { AdminUser, Feature, UsersView } from "../../api/types";
import { useAuth } from "../../auth/useAuth";
import { when } from "./bits";

/**
 * The realm's users. Keycloak owns them; this is a narrow window onto it.
 *
 * Three things a row can have done to it: its rights changed -- which of
 * the features (favorites, playlists, sharing) the person may use; its role
 * changed -- an admin holds every feature by being admin; and it can be
 * blocked, which is Keycloak's enabled flag: a blocked person's next token
 * request is refused, and what they marked and made is kept for when they
 * are unblocked. There is no delete, on purpose.
 *
 * A generated password is shown once, here, and kept nowhere -- the same
 * promise create-user.sh makes, in the same words.
 */
export function Users() {
  const auth = useAuth();
  const [view, setView] = useState<UsersView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ username: string; password?: string; what: string } | null>(null);
  const [draft, setDraft] = useState({
    username: "", email: "", role: "user" as "user" | "admin", password: "", rights: [] as string[],
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    admin.users().then((v) => {
      setView(v);
      // A new user starts with every feature ticked; the admin unticks.
      setDraft((d) => d.rights.length === 0 && !d.username ? { ...d, rights: v.features.map((f) => f.key) } : d);
    }).catch((e: Error) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try { await fn(); load(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const username = draft.username.trim();
    if (!username) return;
    await act(async () => {
      const r = await admin.createUser({
        username, email: draft.email.trim() || undefined, role: draft.role,
        password: draft.password || undefined,
        rights: draft.role === "admin" ? undefined : draft.rights,
      });
      setMade({ username, password: r.password, what: "created" });
      setDraft({ username: "", email: "", role: "user", password: "", rights: view?.features.map((f) => f.key) ?? [] });
    });
  }

  const me = auth.user?.profile?.sub;
  const admins = view?.users.filter((u) => u.enabled && u.roles.includes("admin")).length ?? 0;
  const features = view?.features ?? [];

  return (
    <section className="panel-section users">
      <h2>Users</h2>
      <p className="dim small">
        Who can sign in, what they may do, and who may open this panel.
        Everyone with a login reads the catalogue; the <b>rights</b> say which
        features beyond that a person has. An <code>admin</code> holds every
        right and this panel too. A password is set once, shown once, and not
        kept — write it down. <b>Block</b> refuses a person's next sign-in and
        keeps what they marked and made for when they are unblocked.
      </p>

      {view && !view.configured && (
        <div className="note-box">
          <b>Not configured.</b> {view.note}
        </div>
      )}
      {error && <div className="error">{error}</div>}

      {made && (
        <div className="made">
          <b>{made.username}</b> {made.what}.
          {made.password ? (
            <>
              {" "}Password: <code className="pw">{made.password}</code>
              <button className="link" onClick={(e) => {
                const b = e.currentTarget;
                void copyText(made.password!).then((ok) => { b.textContent = ok ? "copied" : "could not copy"; });
              }}>copy</button>
              <div className="dim small">Not stored anywhere — write it down now.</div>
            </>
          ) : (
            <span className="dim small"> The password you typed is set.</span>
          )}
          <button className="link" onClick={() => setMade(null)}>dismiss</button>
        </div>
      )}

      {view?.configured && (
        <>
          <table className="runs">
            <thead>
              <tr><th>username</th><th>email</th><th>role</th><th>rights</th><th>last seen</th><th>state</th><th /></tr>
            </thead>
            <tbody>
              {view.users.map((u) => (
                <Row key={u.id} u={u} self={u.id === me} lastAdmin={admins <= 1 && u.roles.includes("admin")}
                     features={features} busy={busy}
                     onReset={() => act(async () => {
                       const r = await admin.resetPassword(u.id);
                       setMade({ username: u.username, password: r.password, what: "password reset" });
                     })}
                     onRole={(role) => act(() => admin.setRole(u.id, role))}
                     onBlock={(block) => act(() => admin.setEnabled(u.id, !block))}
                     onRights={(rights) => act(() => admin.setRights(u.id, rights))} />
              ))}
              {view.users.length === 0 && (
                <tr><td colSpan={7} className="dim">No users in the realm.</td></tr>
              )}
            </tbody>
          </table>

          <form className="addrow useradd" onSubmit={(e) => void create(e)}>
            <input value={draft.username} placeholder="username" required
                   onChange={(e) => setDraft({ ...draft, username: e.target.value })} />
            <input value={draft.email} placeholder="email (optional)" type="email"
                   onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value as "user" | "admin" })}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
            <input value={draft.password} placeholder="password (blank = generate)" type="text"
                   minLength={8} autoComplete="off"
                   onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
            {/* The rights are chosen here, not defaulted server-side: adding a
                person means saying what they may do. An admin needs none. */}
            <RightsPicker features={features} value={draft.role === "admin" ? features.map((f) => f.key) : draft.rights}
                          disabled={draft.role === "admin"}
                          onChange={(rights) => setDraft({ ...draft, rights })} />
            <button type="submit" className="primary" disabled={busy || !draft.username.trim()}>Add user</button>
          </form>
        </>
      )}
      {!view && !error && <div className="loading"><span className="spinner" />loading…</div>}
    </section>
  );
}

/** Checkboxes, one per feature. Disabled for an admin, who has them all. */
function RightsPicker({ features, value, disabled, onChange }: {
  features: Feature[];
  value: string[];
  disabled?: boolean;
  onChange: (rights: string[]) => void;
}) {
  return (
    <span className={`rights${disabled ? " all" : ""}`} title={disabled ? "an admin holds every right" : undefined}>
      {features.map((f) => {
        const on = value.includes(f.key);
        return (
          <label key={f.key} className={`chk${on ? " on" : ""}`} title={f.help}>
            <input type="checkbox" checked={on} disabled={disabled}
                   onChange={(e) => onChange(e.target.checked
                     ? features.map((x) => x.key).filter((k) => k === f.key || value.includes(k))
                     : value.filter((k) => k !== f.key))} />
            {f.label}
          </label>
        );
      })}
    </span>
  );
}

function Row({ u, self, lastAdmin, features, busy, onReset, onRole, onBlock, onRights }: {
  u: AdminUser;
  self: boolean;
  lastAdmin: boolean;
  features: Feature[];
  busy: boolean;
  onReset: () => void;
  onRole: (role: "user" | "admin") => void;
  onBlock: (block: boolean) => void;
  onRights: (rights: string[]) => void;
}) {
  const isAdmin = u.roles.includes("admin");
  const none = u.roles.length === 0;
  // The API refuses these too; greying them here just says so up front.
  const locked = self || (lastAdmin && isAdmin);
  return (
    <tr className={u.enabled ? "" : "off"}>
      <td><b>{u.username}</b>{self && <span className="dim small"> (you)</span>}</td>
      <td className="dim">{u.email || "—"}</td>
      <td>
        {none ? <span className="state failed" title="holds neither role, so cannot sign in">none</span>
              : <span className={`state ${isAdmin ? "running" : "succeeded"}`}>{isAdmin ? "admin" : "user"}</span>}
      </td>
      <td>
        {/* Edited in place: a tick is a save. An admin's are every feature
            and cannot be edited -- make them a user first. */}
        <RightsPicker features={features} value={u.rights} disabled={isAdmin || busy || !u.enabled}
                      onChange={onRights} />
      </td>
      <td className="dim">{when(u.lastSeen)}</td>
      <td>
        {u.enabled ? <span className="state succeeded">active</span>
                   : <span className="state failed" title="cannot sign in until unblocked">blocked</span>}
      </td>
      <td className="ops">
        <button className="link" disabled={busy} onClick={onReset}>reset password</button>
        {isAdmin
          ? <button className="link" disabled={busy || locked} onClick={() => onRole("user")}
                    title={self ? "you cannot demote yourself" : lastAdmin ? "the last admin" : "take the admin role away"}>make user</button>
          : <button className="link" disabled={busy} onClick={() => onRole("admin")}>make admin</button>}
        {u.enabled
          ? <button className="link danger" disabled={busy || locked} onClick={() => onBlock(true)}
                    title={self ? "you cannot block yourself" : lastAdmin && isAdmin ? "the last admin" : "refuse their next sign-in; keeps what they made"}>block</button>
          : <button className="link" disabled={busy} onClick={() => onBlock(false)} title="let them sign in again">unblock</button>}
      </td>
    </tr>
  );
}
